import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
} from "react-bootstrap";
import { adminGet, adminPost, adminPut, adminDelete } from "../../../Services";

const AnnouncementAdmin = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMessage, setViewMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    message: "",
    link: "",
    expiryDate: "",
  });
  const [editId, setEditId] = useState(null);

  // Fetch all announcements
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await adminGet("api/announcements");
      setAnnouncements(res.data.data);
    } catch (err) {
      console.error("Error fetching announcements", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Open modal for new or edit
  const openModal = (announcement = null) => {
    if (announcement) {
      setEditMode(true);
      setEditId(announcement.id);
      setFormData({
        title: announcement.title,
        date: announcement.date,
        message: announcement.message,
        link: announcement.link || "",
        expiryDate: announcement.expiryDate
          ? announcement.expiryDate.substring(0, 10)
          : "",
      });
    } else {
      setEditMode(false);
      setFormData({
        title: "",
        date: "",
        message: "",
        link: "",
        expiryDate: "",
      });
    }
    setShowModal(true);
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await adminPut(`api/announcements/${editId}`, formData);
      } else {
        await adminPost("api/announcements", formData);
      }
      fetchAnnouncements();
      setShowModal(false);
    } catch (err) {
      console.error("Error saving announcement", err);
    }
  };

  // Delete announcement
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?"))
      return;
    try {
      await adminDelete(`api/announcements/${id}`);
      fetchAnnouncements();
    } catch (err) {
      console.error("Error deleting announcement", err);
    }
  };

  return (
    <div className="">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="fw-medium" style={{ color: "#1a5276" }}>
          📢 Announcements
        </h3>
        <Button variant="primary" onClick={() => openModal()}>
          + New Announcement
        </Button>
      </div>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <Table bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>Title</th>
              <th>Date</th>
              <th>Message</th>
              <th>Link</th>
              <th>Expiry</th>
              <th>Status</th>
              <th style={{ width: "150px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {announcements.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No announcements found.
                </td>
              </tr>
            ) : (
              announcements.map((ann) => {
                const isExpired =
                  ann.expiryDate && new Date(ann.expiryDate) < new Date();
                return (
                  <tr key={ann.id}>
                    <td>{ann.title}</td>
                    <td>{ann.date}</td>
                    <td style={{ maxWidth: "300px" }}>
                      {ann.message.length > 80 ? (
                        <>
                          {ann.message.substring(0, 80)}...{" "}
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => setViewMessage(ann.message)}
                          >
                            Read More
                          </Button>
                        </>
                      ) : (
                        ann.message
                      )}
                    </td>

                    <td>
                      {ann.link ? (
                        <a
                          href={ann.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Link
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {ann.expiryDate
                        ? new Date(ann.expiryDate).toLocaleDateString()
                        : "No Expiry"}
                    </td>
                    <td>
                      {isExpired ? (
                        <Badge bg="secondary">Expired</Badge>
                      ) : (
                        <Badge bg="success">Active</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="warning"
                        className="me-2"
                        onClick={() => openModal(ann)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(ann.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      {/* Modal for Add/Edit */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? "✏️ Edit Announcement" : "➕ New Announcement"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Date (e.g. December 10, 2025)</Form.Label>
                  <Form.Control
                    type="text"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Link (optional)</Form.Label>
                  <Form.Control
                    type="url"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Expiry Date (optional)</Form.Label>
                  <Form.Control
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="text-end">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="ms-2">
                {editMode ? "Update" : "Create"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal for viewing full message */}
      <Modal show={!!viewMessage} onHide={() => setViewMessage(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Full Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{viewMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewMessage(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AnnouncementAdmin;

